import type { SensitivityATBRow } from '@app/core/patient-requests';

export interface AtbCatalogRow {
  testRow: number;
  atbNr: number;
  atbName: string;
  printSeq: number;
}

export interface SensitivityColumnMeta {
  requisCol: number;
  requisNr: string;
  extRequisNr: string;
  entryDate: string | Date | null;
  isolate: string;
  materialName: string;
}

export interface PivotCellState {
  result: string;
  backGroundColor: string | null;
}

export interface SensitivityPivotModel {
  /** Filas catálogo (RequisCol = 0), ordenadas por PrintSeq. */
  catalog: AtbCatalogRow[];
  /** Columnas datos (col 1 … maxCol) con meta de cabecera. */
  columns: SensitivityColumnMeta[];
  cells: Record<string, PivotCellState>;
}

/**
 * Transforma RS2 plano según DSI-002-A1 §6.7: filas catálogo (RequisCol=0), celdas (RequisCol>0).
 */
export function buildSensitivityPivot(rows: readonly SensitivityATBRow[]): SensitivityPivotModel {
  const catalog = rows
    .filter((r) => r.requisCol === 0)
    .map((r) => {
      const raw = r as SensitivityATBRow & { aTBName?: string; aTBNr?: number };
      const nameTrim = String(raw.atbName ?? '').trim();
      const name = nameTrim || String(raw.aTBName ?? '').trim();
      const nr = Number(raw.atbNr ?? raw.aTBNr ?? 0);
      return {
        testRow: Number(r.testRow),
        atbNr: Number.isNaN(nr) ? 0 : nr,
        atbName: name,
        printSeq: Number(r.printSeq) || 0,
      };
    })
    .sort((a, b) => a.printSeq - b.printSeq || a.testRow - b.testRow);

  const cellsData = rows.filter((r) => r.requisCol > 0);
  const requisCols = [...new Set(cellsData.map((r) => r.requisCol))].sort((a, b) => a - b);

  const columns: SensitivityColumnMeta[] = [];
  const seen = new Set<number>();

  for (const rc of requisCols) {
    const probe = cellsData.find((r) => r.requisCol === rc);
    if (!probe || seen.has(rc)) continue;
    seen.add(rc);
    columns.push({
      requisCol: rc,
      requisNr: String(probe.requisNr ?? '').trim(),
      extRequisNr: String(probe.extRequisNr ?? '').trim(),
      entryDate: probe.entryDate,
      isolate: String(probe.isolate ?? '').trim(),
      materialName: String(probe.materialName ?? '').trim(),
    });
  }

  const cells: Record<string, PivotCellState> = {};
  for (const r of cellsData) {
    const key = cellKey(r.requisCol, r.testRow);
    cells[key] = {
      result: String(r.result ?? '').trim(),
      backGroundColor: r.backGroundColor != null ? String(r.backGroundColor).trim() : null,
    };
  }

  return { catalog, columns, cells };
}

export function cellKey(requisCol: number, testRow: number): string {
  return `${requisCol}|${testRow}`;
}

export function lookupCell(model: SensitivityPivotModel, requisCol: number, testRow: number): PivotCellState {
  return (
    model.cells[cellKey(requisCol, testRow)] ?? {
      result: '',
      backGroundColor: null,
    }
  );
}
